// /managers/ConvoManager/PreflightInjectables.jsx
/* ========================
 * IMPORTS
 * ======================== */
import { v4 as uuidv4 } from 'uuid';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { PIN_TYPES, pinManager } from '@/components/tools/pinned/PinManager';
import { apiManager } from '@/components/layout/RightPanel/shelbulaworkspaces/apiManager';
import { normalizeWorkspaceContextId } from '@/utils/workspaceContext';
import { formatFileContentForLlm } from './MergeFileContentWithMessages';

const LOG_PREFIX = '💉 [Injectables]';

/* ========================
 * INJECT_LOCATION - Defines where content can be injected.
 * ======================== */
const INJECT_LOCATION = {
    APPEND_TO_SYSTEM: { id: 1, name: 'Append To System Message' },
    USER_HEADER: { id: 10, name: 'User Message Header' },
    USER_CONTENT: { id: 11, name: 'User Message Content' },
    USER_FOOTER: { id: 12, name: 'User Message Footer' },
    ASSISTANT_PRIMER: { id: 15, name: 'AssistantPrimer' },
};

/* ========================
 * INJECTION_CONFIG - Configuration for standard injectable content.
 * ======================== */
const INJECTION_CONFIG = {
    METADATA: { location: INJECT_LOCATION.APPEND_TO_SYSTEM, format: { header: '==== META DATA ABOUT USER ====', footer: '========\n' }},
    PAR_MANIFEST: { location: INJECT_LOCATION.USER_HEADER },
    PINNED_ITEMS: {
        location: INJECT_LOCATION.APPEND_TO_SYSTEM,
        format: {
            header: '==== PINNED ITEMS ====\nThe user has pinned these items for your conversation:\n',
            footer: '==== END PINNED ITEMS ====\n\n'
        }
    }
};

const hasMeaningfulContent = (value) => typeof value === 'string' && value.trim() !== '';

const hasMeaningfulBlocks = (msg) => Array.isArray(msg?.contentBlocks)
    && msg.contentBlocks.some((block) => hasMeaningfulContent(block?.content));

const shouldIncludeHistoricalMessage = (msg, currentUserMessageId) => {
    if (!msg || msg.id === currentUserMessageId) return false;

    const isEmptyOptimisticAssistant = msg.role === 'assistant'
        && (msg.isOptimistic === true || msg.status === 'inflight' || msg.status === 'streaming')
        && !hasMeaningfulContent(msg.rawContent)
        && !hasMeaningfulContent(msg.content)
        && !hasMeaningfulContent(msg.contentForUI)
        && !hasMeaningfulBlocks(msg);

    return !isEmptyOptimisticAssistant;
};

/* ========================
 * getUserMetadata - Gathers and formats user-specific metadata.
 * ======================== */
const getUserMetadata = (settings) => {
    try {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = new Date();
        const utcDate = now.toISOString().slice(0, 10);
        const utcTime = now.toISOString().slice(11, 19) + ' UTC';
        const userLocalDate = now.toLocaleDateString('en-CA', { timeZone: userTimezone });
        const userLocalTime = now.toLocaleTimeString('en-GB', { timeZone: userTimezone, hour12: false });
        const timezoneDisplayLine = `Current Time: ${utcDate} ${utcTime} - Users Current Time: ${userLocalDate} ${userLocalTime} (${userTimezone})`;
        
        const metaLines = [
            authManager.getDisplayName() && `Your user's name is: ${authManager.getDisplayName()}`,
            timezoneDisplayLine,
            settings?.zPreferences?.userMeta?.additionalContext && `Additional Context: ${settings.zPreferences.userMeta.additionalContext}`
        ].filter(Boolean);

        if (metaLines.length > 0) {
            const { header, footer } = INJECTION_CONFIG.METADATA.format;
            return {
                content: `${header}\n${metaLines.join('\n')}\n${footer}`,
                location: INJECTION_CONFIG.METADATA.location
            };
        }
        return null;
    } catch (error) {
        return null;
    }
};

/* ========================
 * getParManifestInjectable - Formats READ-ONLY PAR manifest.
 * ======================== */
const getParManifestInjectable = (parManifest) => {
    if (!parManifest?.manifest) return null;
    const header = '==== READ-ONLY PROJECT MANIFEST (PAR) ====\n';
    const footer = '==== END READ-ONLY PROJECT MANIFEST ====\n\n';
    const manifestContent = JSON.stringify(parManifest.manifest, null, 2);
    return {
        content: `${header}${manifestContent}\n${footer}`,
        location: INJECTION_CONFIG.PAR_MANIFEST.location
    };
};


/* ========================
 * getPinnedItemsInjectable - Constructs injectable for pinned items.
 * ======================== */
const getPinnedItemsInjectable = async (conversation) => {
    try {
        const conversationWorkspaceId = normalizeWorkspaceContextId(conversation?.workspaceContextId);
        const activePins = pinManager.getActivePins(conversation?.id).filter((pin) => {
            if (pin.type !== PIN_TYPES.WORKSPACE_FILE) {
                return true;
            }

            const matchesWorkspace = normalizeWorkspaceContextId(pin.workspaceId) === conversationWorkspaceId;
            const matchesConversation = !pin.conversationId || pin.conversationId === conversation?.id;
            return matchesWorkspace && matchesConversation;
        });

        if (!activePins?.length) return null;

        const { header, footer } = INJECTION_CONFIG.PINNED_ITEMS.format;
        const pinnedBlocks = await Promise.all(activePins.map(async (pin) => {
            if (pin.type === PIN_TYPES.WORKSPACE_FILE && pin.workspaceId && pin.filePath) {
                try {
                    const result = await apiManager.readFileContent(pin.workspaceId, pin.filePath);
                    const liveContent = typeof result?.content === 'string' ? result.content : '';
                    if (!liveContent.trim()) {
                        return null;
                    }

                    return `==== ${pin.title || pin.filePath} ====\n${liveContent}\n==== END ${pin.title || pin.filePath} ====\n\n`;
                } catch (error) {
                    return `==== ${pin.title || pin.filePath} ====\n[Pinned workspace file could not be read: ${error?.message || 'Unknown error'}]\n==== END ${pin.title || pin.filePath} ====\n\n`;
                }
            }

            if (typeof pin?.content !== 'string' || !pin.content.trim()) {
                return null;
            }

            return `==== ${pin.title} ====\n${pin.content}\n==== END ${pin.title} ====\n\n`;
        }));

        const resolvedBlocks = pinnedBlocks.filter(Boolean);
        if (!resolvedBlocks.length) return null;

        let content = header;
        content += resolvedBlocks.join('');
        content += footer;
        return { content, location: INJECTION_CONFIG.PINNED_ITEMS.location };
    } catch (error) {
        return null;
    }
};


/* ========================
 * injectContent - Helper to manage injection into message lists and system prompts.
 * ======================== */
const injectContent = (historicalMessages, currentUserMessage, injectables, platform, useSystemMessage) => {
    let appendToSystemMessage = '';
    const processedHistorical = [...historicalMessages];

    const systemInjectables = injectables.filter(inj => inj.location === INJECT_LOCATION.APPEND_TO_SYSTEM);
    if (systemInjectables.length > 0) {
        appendToSystemMessage = systemInjectables.map(inj => inj.content).join('\n\n');
    }

    const userHeaderInjectables = injectables.filter(inj => inj.location === INJECT_LOCATION.USER_HEADER);
    if (userHeaderInjectables.length > 0) {
        const headerContent = userHeaderInjectables.map(inj => inj.content).join('\n');
        currentUserMessage.processedLlmContent = `${headerContent}${currentUserMessage.processedLlmContent}`;
    }
    
    // ... other injection locations can be handled here ...

    return {
        processedConversation: [...processedHistorical, currentUserMessage],
        appendToSystemMessage
    };
};

/* ========================
 * getPreflightInjectables - Main orchestrator for collecting and processing injectables.
 * ======================== */
export const getPreflightInjectables = async (
    historicalConversation,
    newUserMessageData,
    platform = ''
) => {
    try {
        // --- RESTORED to original, correct version ---
        const getAllAttachables = (msg) => [
            ...(Array.isArray(msg.files) ? msg.files : Object.values(msg.files || {})),
            ...(msg.mediaItems || msg.currentMediaItems || [])
        ];

        const memorySize = historicalConversation?.memorySize ?? 20;
        const historicalMessages = (historicalConversation?.messages || []).slice(-memorySize);

        const historicalForLlm = historicalMessages
            .filter(msg => shouldIncludeHistoricalMessage(msg, newUserMessageData.id))
            .map(msg => ({
                ...msg,
                processedLlmContent: formatFileContentForLlm(msg.rawContent || '', getAllAttachables(msg), true)
            }));
        
        let currentUserMessage = {
            id: newUserMessageData.id || uuidv4(),
            role: 'user',
            rawContent: newUserMessageData.rawContent || '',
            processedLlmContent: formatFileContentForLlm(newUserMessageData.rawContent || '', getAllAttachables(newUserMessageData), false),
            timestamp: newUserMessageData.timestamp || Date.now(),
        };

        const settings = authManager.getSettings();
        const useSystemMessage = botManager.getBotById(historicalConversation?.botId)?.useSystemMessage ?? false;
        
        const [
            metadata,
            parManifest,
            pinnedItems,
        ] = await Promise.all([
            getUserMetadata(settings),
            getParManifestInjectable(historicalConversation?.parManifest),
            getPinnedItemsInjectable(historicalConversation),
        ]);
        
        const allInjectables = [metadata, parManifest, pinnedItems].filter(Boolean);
        
        const { processedConversation, appendToSystemMessage } = injectContent(
            historicalForLlm,
            currentUserMessage,
            allInjectables,
            platform,
            useSystemMessage
        );

        return { processedConversation, appendToSystemMessage };

    } catch (error) {
        console.error(`${LOG_PREFIX} Critical error in getPreflightInjectables:`, error);
        // Fallback to a safe, non-injected version
        const fallbackHistorical = (historicalConversation?.messages || []).map(m => ({ ...m, processedLlmContent: m.rawContent || '' }));
        const fallbackNewMessage = { ...newUserMessageData, processedLlmContent: newUserMessageData.rawContent || '' };
        return {
            processedConversation: [...fallbackHistorical, fallbackNewMessage],
            appendToSystemMessage: ''
        };
    }
};

export default getPreflightInjectables;